<template>
  <form class="form" novalidate @submit.prevent="handleSubmit(isModal)" @keyup.enter="handleSubmit(isModal)">
    <ion-card class="form-block">
      <div class="form-block-logo">
        <app-logo :show-logo-only="false" />
      </div>

      <ion-segment v-if="!isCustomDomain()" :value="loginType" @ion-change="onLoginTypeChange($event)">
        <ion-segment-button :value="LoginTypeEnum.Form">
          <ion-label>{{ $t('auth.logIn.formTab') }}</ion-label>
        </ion-segment-button>
        <ion-segment-button :value="LoginTypeEnum.Uri">
          <ion-label>{{ $t('auth.logIn.uriTab') }}</ion-label>
        </ion-segment-button>
      </ion-segment>

      <form-input
        v-if="showUriField"
        v-model:value="uriModel"
        type="text"
        :placeholder="$t('auth.logIn.form.uri')"
        :required="true"
        :spellcheck="false"
        autocapitalize="off"
        :disabled="isLoadingNetworkSettings"
      >
        <div v-if="!(form.uri && form.uri.startsWith('http'))" slot="start" class="protocol" v-html="'https://'" />
      </form-input>

      <not-found-data v-if="showNetworkNotFoundError" :text="$t('auth.logIn.networkNotFound')" />

      <div v-if="showUriErrors">
        <form-error name="fullURL" :errors-array="fullURLErrors" />
        <form-error name="uri" :errors-array="uriErrors" />
      </div>

      <form-input
        v-if="showForm"
        v-model:value="form.login"
        :placeholder="$t('auth.logIn.form.login')"
        autocapitalize="off"
        autocomplete="username"
        :spellcheck="false"
      />

      <div v-if="showLoginErrors">
        <form-error name="login" :errors-array="loginErrors" />
      </div>

      <form-input
        v-if="showForm"
        v-model:value="form.password"
        :type="showPassword ? 'text' : 'password'"
        :placeholder="$t('auth.logIn.form.password')"
        autocomplete="current-password"
      >
        <icons-provider
          class="icon"
          :icon-props="{
            width: '20',
            height: '20',
            fill: 'var(--ion-color-medium)',
          }"
          :name="showPassword ? AppIconsEnum.Eye : AppIconsEnum.EyeSlash"
          @click="showPassword = !showPassword"
        />
      </form-input>

      <div v-if="showPasswordErrors">
        <form-error name="password" :errors-array="passwordErrors" />
      </div>

      <service-selector v-if="showServiceSelector" />

      <ion-button
        mode="md"
        fill="solid"
        expand="block"
        type="submit"
        size="default"
        class="form-block-submit_button"
        color="intra"
        :disabled="disableLoginButton"
        :class="!appStore.isDarkTheme(appStore.theme) ? 'light' : 'dark'"
      >
        <icons-provider
          v-if="showLoading"
          :icon-props="{
            width: '24',
            height: '24',
            fill: 'var(--ion-color-custom)',
            strokewidth: 2,
          }"
          :name="AppIconsEnum.CircleAnim"
        />
        <ion-label v-else color="custom">
          {{ $t('auth.logIn.title') }}
        </ion-label>
      </ion-button>

      <login-bottom-section :page-ref="pageRef" />
    </ion-card>
  </form>
</template>

<script lang="ts" setup>
import type { SegmentCustomEvent } from '@ionic/vue';
import { IonButton, IonCard, IonLabel, IonSegment, IonSegmentButton, modalController } from '@ionic/vue';
import type { ErrorObject } from '@vuelidate/core';
import useVuelidate from '@vuelidate/core';
import { watchDebounced, useWindowSize } from '@vueuse/core';
import type { ComponentPublicInstance, ComputedRef, PropType } from 'vue';
import { computed, onBeforeMount, onMounted, onUnmounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';

import {
  AppLogo,
  FormError,
  FormInput,
  ServiceSelector,
  IconsProvider,
  LoginBottomSection,
  NotFoundData,
} from '@/components';
import { AppIconsEnum } from '@/enums';
import { useTheme, isNativeMobile, required, useLayout, normalizeDomain, domain, normalizeHost } from '@/helpers';
import { useAuthFlow } from '@/composables/useAuthFlow';
import { useAutoLogin } from '@/helpers/use/useAutoLoginHelper';
import { resetAllStores, useAppStore, useAuthStore, useNetworkStore, useUserStore } from '@/store';
import { StoresEnum } from '@/enums/stores';
import type { AuthFormModel, NetworkLocateModel } from '@/types';
import { getFullUrl, getServiceHost, isCustomDomain } from '@/api';
import { useI18n } from '@/i18n';
import { LoginTypeEnum, UserVerificationTypeEnum } from '@/enums/auth';
import { openHome, openLoading } from '@/router';
import { useNetworkSettings } from '@/composables/useNetworkSettings';

const props = defineProps({
  pageRef: {
    type: [Object, null] as PropType<ComponentPublicInstance | null>,
    default: null,
  },
  isModal: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  (e: 'loading-settings', value: boolean): void;
}>();

//#region Variables
const router = useRouter();
const { t } = useI18n();
const { width: innerWidth } = useWindowSize();
const { enabled: isAutoLoginEnabled, attempt: attemptAutoLogin } = useAutoLogin();
const { clearTheme, setTheme, getAppColor } = useTheme();
const { login: authLogin, loading: authLoading } = useAuthFlow();
const {
  clear: clearNetworkSettings,
  check: checkNetworkSettings,
  isLoading: isLoadingNetworkSettings,
} = useNetworkSettings();

const appStore = useAppStore();
const { isAuth, setAuthType, setHost, host: getHost } = useAuthStore();
const { isLoading: isLoadingNetworkStore } = useNetworkStore();
const { isLoading: isLoadingUserStore } = useUserStore();

const form: AuthFormModel = reactive({ login: '', password: '', uri: '', fullURL: '' });

const showPassword = ref(false);
const isAutoLocate = ref(false);
const loginType = ref(LoginTypeEnum.Form);
const locateSettings = ref<NetworkLocateModel | null>(null);

const uriError = ref<string | null>(null);
const loginError = ref<string | null>(null);
const passwordError = ref<string | null>(null);
const otherError = ref<string | null>(null);

const isOAuth = computed(() => locateSettings.value?.verificationType === UserVerificationTypeEnum.VerificationByOauth);
const isOAuthApi = computed(
  () => locateSettings.value?.verificationType === UserVerificationTypeEnum.VerificationByOauthApi
);
const isUri = computed(() => loginType.value === LoginTypeEnum.Uri);
const showServiceSelector = computed(() => __DEV__ && loginType.value === LoginTypeEnum.Form);
const showUriField = computed(() => !isCustomDomain() && isUri.value);
const showForm = computed(
  () => loginType.value === LoginTypeEnum.Form || (form.uri && locateSettings.value && !isOAuth.value)
);
const showLoading = computed(
  () => appStore.isLoading || isLoadingNetworkStore || isLoadingUserStore || authLoading.value
);
const disableLoginButton = computed(
  () => showLoading.value || (loginType.value === LoginTypeEnum.Uri && !locateSettings.value)
);

// Show errors
const showNetworkNotFoundError = computed(() => isCustomDomain() && isUri.value && uriError.value);
const showUriErrors = computed(
  () => showUriField.value && (fullURLErrors.value.length > 0 || uriErrors.value.length > 0)
);
const showLoginErrors = computed(() => !isUri.value && loginErrors.value.length > 0);
const showPasswordErrors = computed(() => !isUri.value && passwordErrors.value.length > 0);

// Validation rules
const validationRules = computed(() => {
  if (isUri.value) {
    return {
      login: { required },
      password: { required },
      uri: { required, domain },
    };
  }

  return {
    login: { required },
    password: { required },
  };
});

const v$ = useVuelidate(validationRules, form);

// Error computeds
const loginErrors: ComputedRef<string[]> = computed(() => {
  const validationErrors = v$.value.login.$errors.map((m: ErrorObject) => m.$message as string);
  return loginError.value ? [...validationErrors, loginError.value] : validationErrors;
});

const passwordErrors: ComputedRef<string[]> = computed(() => {
  const validationErrors = v$.value.password.$errors.map((m: ErrorObject) => m.$message as string);
  return passwordError.value ? [...validationErrors, passwordError.value] : validationErrors;
});

const fullURLErrors: ComputedRef<string[]> = computed(() => {
  if (!v$.value?.fullURL) return [];
  return v$.value.fullURL.$errors.map((m: ErrorObject) => m.$message as string);
});

const uriErrors: ComputedRef<string[]> = computed(() => {
  return uriError.value ? [uriError.value] : [];
});

const uriModel = computed({
  get: () => form.uri,
  set: (val: string) => {
    form.uri = normalizeDomain(val);
  },
});
//#endregion

//#region Methods
function clearErrors(): void {
  uriError.value = null;
  loginError.value = null;
  passwordError.value = null;
  otherError.value = null;
}

function clearForm(): void {
  Object.assign(form, { login: '', password: '', uri: '', fullURL: '' } satisfies AuthFormModel);
  clearErrors();
  v$.value.$reset();
}

function triggerSettingsLoading(bool: boolean): void {
  emit('loading-settings', bool);
}

async function clearSettings(): Promise<void> {
  clearNetworkSettings();
  if (loginType.value === LoginTypeEnum.Uri) locateSettings.value = null;
  [useAppStore(), useAuthStore(), useNetworkStore(), useUserStore()].forEach((store) => store.$reset());
  isAutoLocate.value = false;
  await clearTheme();
}

/**
 * @description Fetches settings for the given URI, updates stores/theme,
 * and trigger OAuth popup when required
 */
async function fetchAndApplySettings(uri: string): Promise<void> {
  await v$.value.$validate();
  if (fullURLErrors.value.length !== 0) {
    await clearSettings();
    return;
  }

  if (!isCustomDomain() && uri) {
    setHost(uri);
  }

  triggerSettingsLoading(true);
  const newSettings = await checkNetworkSettings(uri);
  triggerSettingsLoading(false);

  if (!newSettings) {
    uriError.value = t('auth.logIn.networkNotFound');
    await clearSettings();
    return;
  }

  applyNetworkSettings(newSettings);

  // For OAuth, we need to trigger the popup for the user
  if (isOAuth.value) await handleSubmit(props.isModal);
}

/**
 * @description Applies located network settings to reactive refs and stores
 * @note All side‑effects (theme change, auth type, host) live here,
 * keeping composables pure and this component the `imperative shell`
 */
function applyNetworkSettings(newSettings: NetworkLocateModel): void {
  locateSettings.value = newSettings;

  setTheme(getAppColor(newSettings.headBackgroundColor));

  switch (true) {
    case !newSettings || isOAuthApi.value:
      setAuthType('Form');
      break;
    case isOAuth.value:
      setAuthType('External');
      break;
    default:
      setAuthType('Enterprise');
  }

  // For generic domains OR OAuth
  // we need to set the normalized host
  // ~~core-beta2~~ => core
  if (!isCustomDomain() || isOAuth.value) {
    setHost(normalizeHost(newSettings.hostWithSubHost));
  }

  clearErrors();
  v$.value.$reset();
}
/**
 * @description Orchestrates initial page logic:
 * 1. Redirect if already authenticated (page mode)
 * 2. Clear stores if not authenticated / modal mode
 * 3. Attempt auto‑login (page mode only) when enabled
 * 4. Handle custom‑domain bootstrap
 */
async function initLoginFlow(isModal: boolean): Promise<void> {
  const alreadyAuthOnPage = isAuth() && !isModal;
  if (alreadyAuthOnPage) {
    await router.replace(appStore.homePage);
    return;
  }

  if (!isAuth()) {
    await clearSettings();
  }

  const autoLoginOnPage = (await isAutoLoginEnabled()) && !isModal;
  if (autoLoginOnPage) {
    await attemptAutoLogin();
    return;
  }

  if (isCustomDomain()) {
    await setupCustomDomain();
  }
}

async function setupCustomDomain(): Promise<void> {
  isAutoLocate.value = true;
  loginType.value = LoginTypeEnum.Uri;
  form.uri = getHost;

  const fullURL = getFullUrl(getHost);
  if (!fullURL) {
    uriError.value = t('auth.logIn.invalidDomain');
    return;
  }

  form.fullURL = fullURL;
  await fetchAndApplySettings(form.uri);
}

async function onLoginTypeChange(ev: SegmentCustomEvent): Promise<void> {
  const type = ev.detail.value;
  if (!type) return;

  loginType.value = type as LoginTypeEnum;
  clearForm();
  await clearSettings();
}

async function handleSubmit(isModal: boolean): Promise<void> {
  const needNetworkSettings = loginType.value === LoginTypeEnum.Uri && !locateSettings.value;
  if (needNetworkSettings) return;

  const isValid = (isOAuth.value && isUri.value) || (await v$.value.$validate());
  if (!isValid || (!isModal && props.pageRef === null)) return;

  if (isModal) {
    await openLoading();
    await modalController.dismiss();
    resetAllStores([StoresEnum.APP]);
    setHost(getServiceHost());
  }

  await authLogin({
    mode: isOAuth.value && isUri.value ? 'oauth' : isOAuthApi.value ? 'oauthApi' : 'form',
    credentials: isOAuth.value && isUri.value ? undefined : { login: form.login, password: form.password },
    uri: form.uri,
    isModal,
    platform: isNativeMobile ? 'native' : 'web',
  });

  if (isModal) await openHome();
}
//#endregion

//#region Watchers
watchDebounced(
  () => form.uri,
  async (newUri) => {
    clearNetworkSettings();
    locateSettings.value = null;
    if (!newUri) return;
    form.uri = normalizeDomain(newUri);
    form.fullURL = getFullUrl(form.uri) ?? '';
    await fetchAndApplySettings(form.uri);
  },
  { debounce: 400 }
);
//#endregion

//#region Lifecycle
onBeforeMount(async () => {
  showPassword.value = false;
  clearForm();
});

onMounted(async () => {
  await initLoginFlow(props.isModal);
});

onUnmounted(() => {
  useLayout().updateInnerWidth(innerWidth.value);
});
//#endregion
</script>

<style scoped lang="scss">
.app-grid {
  height: 100%;

  &.grid-fixed {
    --ion-grid-padding: 0;
    max-width: app-bp(md);
  }

  .app-col {
    --ion-grid-column-padding: 0;
  }

  .app-row {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.form-block {
  padding: app-padding(lg);
  border-radius: #{app-radius(md)};

  ion-item .icon {
    margin-left: #{app-padding(md)};
    &:hover {
      cursor: pointer;
    }
  }

  ion-button {
    margin-top: 1rem;

    &.form-block-submit_button {
      --box-shadow: none;
      --border-radius: #{app-radius(md)};
      --border-style: solid;
      --border-width: 1px;
      margin: 0;
      margin-top: app-padding(lg);
      white-space: normal;
      color: var(--ion-color-custom);

      &:active::part(native) {
        background: var(--ion-color-intra-activated);
      }

      &.dark {
        --border-color: var(--ion-card-background);
      }

      &.light {
        --border-color: var(--ion-color-light-tint);
      }

      ion-label {
        margin: app-padding(md);
        font-weight: bold;
      }
    }
  }

  &-logo {
    margin-bottom: app-padding(lg);
    margin-top: app-padding(lg);
    display: flex;
    justify-content: center;
    align-items: center;
    svg {
      width: 50%;
    }
  }

  .protocol {
    font-size: 1.1rem;
    margin-right: 0;
  }
}

ion-segment-button {
  --color-checked: var(--ion-color-medium);
  --color-hover: var(--ion-color-medium);
  max-width: 100%;
}
</style>
